/* eslint-disable @typescript-eslint/no-explicit-any */
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';
import {
    useTiersService,
    useFormaliteService,
    useOffreService,
    useUtilisateurService,
    useSignService,
    SousClientsSelector,
    PrescripteursSelector,
    CatalogSelector,
    utilisateursSelector,
    antennesSelector,
    groupesTravailSelector,
    FournisseurSelector,
    DossierSignatureSelector,
    DossierOpenSearchSelector,
    PackageSelector,
    ArticleSelector,
    OfferOptionsSelector,
    OfferAdditionalFieldsSelector,
    DocumentSelector,
    OfferSelector,
    FormalitySelector,
    useFacturationService,
    FactureOpenSearchSelector,
    GroupSelector,
    CollectionOpenSearchSelector,
    useAchatsService,
    ReminderPublisherSelector,
    FactureOpenSearchPrescriberSelector,
} from '@europrocurement/l2d-domain';

import { PossibleTypes } from './SearchBarProvider';

export interface SearchBarConfig {
    placeholder: string;
    selector: (state: any) => any;
    serviceHook: () => any;
    getFetchFunction: (service: any) => getDataThunkType<any>;
    customStyling?: object;
}

export const SearchBarRegistry: Record<PossibleTypes, SearchBarConfig | null> = {
    [PossibleTypes.Society]: {
        placeholder: 'denomination',
        selector: SousClientsSelector,
        serviceHook: useTiersService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useTiersService>).getSousClients,
    },
    [PossibleTypes.Prescriber]: {
        placeholder: 'code client, raison sociale, code postal, contact',
        selector: PrescripteursSelector,
        serviceHook: useTiersService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useTiersService>).getPrescripteurs,
    },
    [PossibleTypes.Publisher]: {
        placeholder: 'siren, libelle',
        selector: FournisseurSelector,
        serviceHook: useTiersService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useTiersService>).getFournisseurs,
    },
    [PossibleTypes.PublisherReminder]: {
        placeholder: 'fournisseur, destinataire, statut, société, ...',
        selector: ReminderPublisherSelector,
        serviceHook: useAchatsService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useAchatsService>).getReminderPublisher,
    },
    [PossibleTypes.ElectronicSign]: {
        placeholder: 'sujet',
        selector: DossierSignatureSelector,
        serviceHook: useSignService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useSignService>).getDossierSignature,
    },
    [PossibleTypes.FormalityFolio]: {
        placeholder: 'numéro, référence, société, contact, prescripteur, statut, formaliste, ...',
        selector: DossierOpenSearchSelector,
        serviceHook: useFormaliteService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useFormaliteService>).getOpenSearchDossiers,
    },
    [PossibleTypes.FormalityOrder]: {
        placeholder: 'numéro, société, prescripteur',
        selector: CollectionOpenSearchSelector,
        serviceHook: useFormaliteService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useFormaliteService>).getOpenSearchCollections,
    },
    [PossibleTypes.FormalityInvoice]: {
        placeholder: 'numéro, référence, société, contact, prescripteur, statut, formaliste, ...',
        selector: FactureOpenSearchSelector,
        serviceHook: useFacturationService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useFacturationService>).getFactureOpenSearch,
    },
    [PossibleTypes.FormalityInvoicePrescriber]: {
        placeholder: 'numéro, référence, société, contact, prescripteur, statut, formaliste, ...',
        selector: FactureOpenSearchPrescriberSelector,
        serviceHook: useFacturationService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useFacturationService>).getFactureOpenSearchPrescriber,
    },
    [PossibleTypes.Formality]: {
        placeholder: 'libellé, catégorie',
        selector: FormalitySelector,
        serviceHook: useFormaliteService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useFormaliteService>).getFormalites,
    },
    [PossibleTypes.Catalogue]: {
        placeholder: 'id, nom commercial, groupement, offre',
        selector: CatalogSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useOffreService>).getCatalogsFull,
    },
    [PossibleTypes.Package]: {
        placeholder: 'id, libellé, nom commercial, type de formalité, domaine',
        selector: PackageSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) => (service as ReturnType<typeof useOffreService>).getPackages,
    },
    [PossibleTypes.Article]: {
        placeholder: 'id, libellé, code article, rubrique',
        selector: ArticleSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) => (service as ReturnType<typeof useOffreService>).getArticles,
    },
    [PossibleTypes.Option]: {
        placeholder: 'id, libellé, description',
        selector: OfferOptionsSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useOffreService>).getOfferOptions,
    },
    [PossibleTypes.ComplementaryField]: {
        placeholder: 'id, libellé, description',
        selector: OfferAdditionalFieldsSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useOffreService>).getOfferAdditionalFields,
    },
    [PossibleTypes.Document]: {
        placeholder: 'libellé',
        selector: DocumentSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useOffreService>).documentModel.datasourcesThunks.main
                .getData,
    },
    [PossibleTypes.Group]: {
        placeholder: 'libellé, domaine',
        selector: GroupSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useOffreService>).offerGroupModel.datasourcesThunks.main
                .getData,
    },
    [PossibleTypes.Offer]: {
        placeholder: 'id, visibilité, libellé',
        selector: OfferSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) => (service as ReturnType<typeof useOffreService>).getOffers,
    },
    [PossibleTypes.PricesGrid]: {
        placeholder: 'numéro, offre, formalités',
        selector: OfferSelector,
        serviceHook: useOffreService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useOffreService>).getCatalogsFull,
    },
    [PossibleTypes.User]: {
        placeholder: 'nom, login, email',
        selector: utilisateursSelector,
        serviceHook: useUtilisateurService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useUtilisateurService>).getUtilisateurs,
    },
    [PossibleTypes.Antenne]: {
        placeholder: 'libelle',
        selector: antennesSelector,
        serviceHook: useUtilisateurService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useUtilisateurService>).getAntennes,
    },
    [PossibleTypes.WorkGroup]: {
        placeholder: 'référent',
        selector: groupesTravailSelector,
        serviceHook: useUtilisateurService,
        getFetchFunction: (service) =>
            (service as ReturnType<typeof useUtilisateurService>).getGroupesTravail,
    },
};
